import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext';
import { api, photoPreviewUrl, photoUrl, type Comment, type Photo } from '../lib/api';
import { CloseIcon, SendIcon } from './Icons';
import { Avatar } from './Avatar';

type CommentView = Comment & { delivery?: 'posting' };

/**
 * Full-screen photo viewer with the comment thread.
 *
 * Swipe left/right moves between photos, matching what people expect from a
 * phone gallery. Comments come from Nextcloud, so anything written here is
 * visible in the Nextcloud and Memories web UI too.
 */
export function Lightbox({
  photos,
  index,
  initialPreviewSize = 1200,
  onClose,
  onIndexChange,
}: {
  photos: Photo[];
  index: number;
  /** Size already visible on the surface that opened this post. */
  initialPreviewSize?: number;
  onClose: () => void;
  onIndexChange: (next: number) => void;
}) {
  const { user } = useAuth();
  const photo = photos[index];

  const [comments, setComments] = useState<CommentView[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [loadedPreviewId, setLoadedPreviewId] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const commentsRef = useRef<HTMLDivElement>(null);
  const hasHistoryEntry = useRef(false);
  const activeFileIdRef = useRef<string | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const loadComments = useCallback(async (fileId: string) => {
    setLoadingComments(true);
    try {
      const result = await api.comments(fileId);
      if (activeFileIdRef.current !== fileId) return;
      setComments(result.comments);
      setCommentError(null);
    } catch {
      if (activeFileIdRef.current !== fileId) return;
      setComments([]);
      setCommentError('Comments could not load. Try again in a moment.');
    } finally {
      if (activeFileIdRef.current === fileId) setLoadingComments(false);
    }
  }, []);

  useEffect(() => {
    activeFileIdRef.current = photo?.fileId ?? null;
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    setComments([]);
    setDraft('');
    setSending(false);
    setCommentError(null);
    if (photo?.fileId) void loadComments(photo.fileId);
  }, [photo?.fileId, loadComments]);

  const goTo = useCallback(
    (next: number) => {
      if (next >= 0 && next < photos.length) onIndexChange(next);
    },
    [photos.length, onIndexChange],
  );

  const close = useCallback(() => {
    const state = window.history.state as { familyPhotosLightbox?: boolean } | null;
    if (hasHistoryEntry.current && state?.familyPhotosLightbox) {
      window.history.back();
      return;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') goTo(index - 1);
      if (event.key === 'ArrowRight') goTo(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, goTo, close]);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  /**
   * A focused post is a temporary layer over the feed, not a new destination.
   * Give it an entry in browser history so the Android/iOS Back affordance
   * dismisses this layer first instead of taking someone out of the feed.
   */
  useEffect(() => {
    const state = window.history.state as { familyPhotosLightbox?: boolean } | null;
    if (!state?.familyPhotosLightbox) {
      // A hash makes this a distinct same-document entry on mobile browsers
      // that otherwise coalesce state-only changes in their Back stack.
      window.history.pushState(
        { ...(state ?? {}), familyPhotosLightbox: true },
        '',
        `${window.location.pathname}${window.location.search}#post`,
      );
      hasHistoryEntry.current = true;
    }

    const onPopState = () => onClose();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onClose]);

  /** Lock the feed and hide its raised Share control during immersive focus. */
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.classList.add('post-view-open');
    return () => {
      document.body.style.overflow = previous;
      document.documentElement.classList.remove('post-view-open');
    };
  }, []);

  if (!photo) return null;

  const submit = async () => {
    const message = draft.trim();
    if (!message || sending) return;

    const fileId = photo.fileId;
    const optimisticId = `posting-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const optimisticComment: CommentView = {
      id: optimisticId,
      message,
      actorId: user?.id ?? 'current-user',
      actorDisplayName: user?.displayName ?? 'You',
      createdAt: Date.now(),
      delivery: 'posting',
    };

    // Paint the write immediately. Nextcloud remains authoritative and either
    // confirms this row or the catch path removes it and restores the draft.
    setComments((current) => [...current, optimisticComment]);
    setDraft('');
    setSending(true);
    setCommentError(null);
    try {
      await api.addComment(fileId, message);
      if (activeFileIdRef.current !== fileId) return;

      setComments((current) => current.map((comment) => comment.id === optimisticId
        ? { ...comment, delivery: undefined }
        : comment));

      // Reconcile in the background so the home-server read latency never
      // blocks the composer or makes a successful comment appear to vanish.
      void api.comments(fileId).then((result) => {
        if (activeFileIdRef.current !== fileId) return;
        const isPresent = result.comments.some((comment) => (
          comment.actorId === (user?.id ?? 'current-user')
          && comment.message === message
          && Math.abs(comment.createdAt - optimisticComment.createdAt) < 120_000
        ));
        if (isPresent) setComments(result.comments);
      }).catch(() => {
        // The POST succeeded. Keep the confirmed local row until the next open
        // rather than replacing success with a misleading read error.
      });
    } catch {
      if (activeFileIdRef.current === fileId) {
        setComments((current) => current.filter((comment) => comment.id !== optimisticId));
        setDraft((current) => current || message);
        setCommentError('Your comment was not posted. It is back in the box so you can retry.');
      }
    } finally {
      if (activeFileIdRef.current === fileId) setSending(false);
    }
  };

  const showComments = () => {
    commentsRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label={photo.name}>
      <div className="lightbox-header">
        <button ref={closeRef} type="button" className="icon-btn" onClick={close} aria-label="Close">
          <CloseIcon />
        </button>
        <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)' }}>
          {index + 1} of {photos.length}
        </span>
        <a
          className="icon-btn"
          href={photoUrl(photo)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open original image"
          title="Open original"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ width: 20, height: 20 }}
            aria-hidden="true"
          >
            <path d="M15 3h6v6M10 14 21 3M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
          </svg>
        </a>
      </div>

      <div className="lightbox-scroll" ref={scrollRef}>
        <div
          className="lightbox-body"
          onTouchStart={(event) => {
            touchStartX.current = event.touches[0]?.clientX ?? null;
            touchStartY.current = event.touches[0]?.clientY ?? null;
          }}
          onTouchEnd={(event) => {
            const startX = touchStartX.current;
            const startY = touchStartY.current;
            if (startX === null || startY === null) return;

            const deltaX = (event.changedTouches[0]?.clientX ?? startX) - startX;
            const deltaY = (event.changedTouches[0]?.clientY ?? startY) - startY;

            // Only treat it as a swipe if it was clearly horizontal, so a
            // vertical scroll toward the comments does not change photo.
            if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
              goTo(deltaX < 0 ? index + 1 : index - 1);
            }

            touchStartX.current = null;
            touchStartY.current = null;
          }}
        >
          {photo.isVideo ? (
            <video
              key={photo.fileId}
              src={photoUrl(photo)}
              poster={photoPreviewUrl(photo, initialPreviewSize)}
              controls
              playsInline
              preload="metadata"
            />
          ) : (
            <>
              {initialPreviewSize !== 2048 && (
                <img
                  className="lightbox-preview-placeholder"
                  src={photoPreviewUrl(photo, initialPreviewSize)}
                  alt=""
                  aria-hidden="true"
                  decoding="sync"
                />
              )}
              <img
                key={photo.fileId}
                // Keep the already-cached surface preview visible until this
                // full-detail derivative is ready.
                className={initialPreviewSize !== 2048 ? `lightbox-full-preview ${loadedPreviewId === photo.fileId ? 'loaded' : ''}` : undefined}
                src={photoPreviewUrl(photo, 2048)}
                alt={photo.name}
                decoding="async"
                onLoad={() => setLoadedPreviewId(photo.fileId)}
                onError={() => setLoadedPreviewId(photo.fileId)}
              />
            </>
          )}
          <button type="button" className="lightbox-comments-cue" onClick={showComments} aria-label="Show comments">
            <span>Comments</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m7 10 5 5 5-5" />
            </svg>
          </button>
        </div>

        <div className="lightbox-footer" ref={commentsRef}>
          <div className="lightbox-details">
          {photo.caption && (
            <p className="lightbox-caption">
              <strong>{photo.owner || 'Family'}</strong> {photo.caption}
            </p>
          )}
          <div className="lightbox-meta">
            {photo.album ? `${photo.album} · ` : ''}
            {new Date(photo.lastModified).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </div>

          {commentError && <p className="comment-error" role="alert">{commentError}</p>}

          {loadingComments && comments.length === 0 ? (
            <div className="comment-empty" role="status">Loading comments…</div>
          ) : comments.length === 0 ? (
            <div className="comment-empty">No comments yet. Start the conversation.</div>
          ) : (
            comments.map((comment) => (
              <div className={`comment ${comment.delivery === 'posting' ? 'posting' : ''}`} key={comment.id}>
                <Avatar userId={comment.actorId} displayName={comment.actorDisplayName} size={30} className="comment-avatar" />
                <div className="comment-content">
                  <div>
                    <span className="comment-author">{comment.actorDisplayName}</span>
                    <span className="comment-time">{comment.delivery === 'posting' ? 'Posting…' : relativeTime(comment.createdAt)}</span>
                  </div>
                  <div className="comment-body">{comment.message}</div>
                </div>
              </div>
            ))
          )}
        </div>

          <div className="composer lightbox-comment-composer">
            <textarea
              rows={1}
              value={draft}
              placeholder="Add a comment…"
              aria-label="Add a comment"
              disabled={sending}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void submit();
                }
              }}
            />
            <button
              type="button"
              className="comment-send"
              onClick={() => void submit()}
              disabled={!draft.trim() || sending}
              aria-label={sending ? 'Posting comment' : 'Post comment'}
              aria-busy={sending}
            >
              <SendIcon className="" />
              <span>{sending ? 'Posting…' : 'Post'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function relativeTime(timestamp: number): string {
  if (!timestamp) return '';

  const seconds = Math.round((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}
